import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 蒸汽裂解装置 P&ID 详细图（准则第 4 节生成顺序：主体设备→辅助→连线→lint） */
const media = {
  oil: { label: '石脑油 / 柴油', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  pygas: { label: '裂解气 / 乙烯', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  steam: { label: '蒸汽', light: '#d32f2f', dark: '#e57373', width: 2 },
  water: { label: '水', light: '#1565c0', dark: '#64b5f6', width: 2 },
  fuel: { label: '燃料气', light: '#c2185b', dark: '#f06292', width: 2, dash: '7 4' },
};

const devices = [
  // 主线 handle 中线 y=233：y = 233 - svgH/2 - labelH/2（两行 32 / 三行 45）
  { id: 'v101', kind: 'hDrum' as const, x: 170, y: 189, tag: 'V-101', name: '石脑油/柴油缓冲罐' },
  { id: 'mix101', kind: 'mixer' as const, x: 360, y: 225, tag: '', name: '' },
  { id: 'f101', kind: 'furnace' as const, x: 430, y: 146, tag: 'F-101', name: '裂解炉', note: '出口 850℃' },
  { id: 'e101', kind: 'exchanger' as const, x: 590, y: 181, tag: 'E-101', name: '急冷锅炉', note: '副产超高压汽' },
  { id: 't101', kind: 'column' as const, x: 720, y: 101, tag: 'T-101', name: '急冷塔' },
  { id: 'c101', kind: 'compressor' as const, x: 880, y: 193, tag: 'C-101', name: '裂解气压缩机' },
  { id: 't102', kind: 'column' as const, x: 1030, y: 101, tag: 'T-102', name: '碱洗塔' },
  { id: 'd101', kind: 'vDrum' as const, x: 1170, y: 161, tag: 'D-101', name: '干燥器' },
  { id: 't201', kind: 'column' as const, x: 1300, y: 101, tag: 'T-201', name: '脱甲烷塔' },
  { id: 't202', kind: 'column' as const, x: 1440, y: 101, tag: 'T-202', name: '脱乙烷塔' },
  { id: 't203', kind: 'column' as const, x: 1580, y: 101, tag: 'T-203', name: '乙烯精馏塔' },
  { id: 't204', kind: 'column' as const, x: 1740, y: 101, tag: 'T-204', name: '脱丙烷塔' },
  { id: 't205', kind: 'column' as const, x: 1880, y: 101, tag: 'T-205', name: '丙烯精馏塔' },
];

const ports: PidPort[] = [
  { id: 'p-feed', label: '石脑油/柴油', medium: 'oil', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-ds', label: '稀释蒸汽', medium: 'steam', x: 300, y: 330, direction: 'in' as const },
  { id: 'p-fg', label: '燃料气', medium: 'fuel', x: 250, y: 648, direction: 'in' as const },
  { id: 'p-stmout', label: '副产超高压蒸汽', medium: 'steam', x: 720, y: 52, direction: 'out' as const },
  { id: 'p-meth', label: '甲烷氢', medium: 'pygas', x: 2140, y: 52, direction: 'out' as const },
  { id: 'p-ethylene', label: '乙烯', medium: 'pygas', x: 2150, y: 611, direction: 'out' as const },
  { id: 'p-propylene', label: '丙烯', medium: 'oil', x: 2150, y: 681, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'mix101', toSide: 'left', medium: 'oil' },
  { id: 's03', from: 'p-ds', fromSide: 'right', to: 'mix101', toSide: 'bottom', medium: 'steam' },
  { id: 's04', from: 'mix101', fromSide: 'right', to: 'f101', toSide: 'left', medium: 'oil' },
  { id: 's05', from: 'f101', fromSide: 'right', to: 'e101', toSide: 'left', medium: 'pygas' },
  { id: 's06', from: 'e101', fromSide: 'right', to: 't101', toSide: 'left', medium: 'pygas' },
  { id: 's07', from: 't101', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'pygas' },
  { id: 's08', from: 'c101', fromSide: 'right', to: 't102', toSide: 'left', medium: 'pygas' },
  { id: 's09', from: 't102', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'pygas' },
  { id: 's10', from: 'd101', fromSide: 'right', to: 't201', toSide: 'left', medium: 'pygas' },
  { id: 's11', from: 't201', fromSide: 'right', to: 't202', toSide: 'left', medium: 'pygas' },
  { id: 's12', from: 't202', fromSide: 'right', to: 't203', toSide: 'left', medium: 'pygas' },
  { id: 's13', from: 't203', fromSide: 'right', to: 't204', toSide: 'left', medium: 'pygas' },
  { id: 's14', from: 't204', fromSide: 'right', to: 't205', toSide: 'left', medium: 'pygas' },
  // 甲烷氢：脱甲烷塔顶走顶部走廊
  { id: 's15', from: 't201', fromSide: 'top', to: 'p-meth', toSide: 'left', medium: 'pygas' },
  // 乙烯/丙烯：重组分走底部车道
  { id: 's16', from: 't203', fromSide: 'bottom', to: 'p-ethylene', toSide: 'left', medium: 'pygas' },
  { id: 's17', from: 't205', fromSide: 'bottom', to: 'p-propylene', toSide: 'left', medium: 'oil' },
  // 副产超高压蒸汽（轻组分走上面）
  { id: 's18', from: 'e101', fromSide: 'top', to: 'p-stmout', toSide: 'left', medium: 'steam' },
  { id: 's19', from: 'p-fg', fromSide: 'right', to: 'f101', toSide: 'bottom', medium: 'fuel' },
];

const SteamCrackingDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="蒸汽裂解装置详细流程"
    subtitle="裂解炉（750~900℃）→ 急冷锅炉（副产超高压蒸汽）→ 急冷塔 → 裂解气压缩 → 碱洗干燥 → 深冷分离塔系（脱甲烷→乙烯塔→丙烯塔），稀释蒸汽与石脑油混合进炉（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default SteamCrackingDiagram;
